import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { compact, flatten, flow, get } from 'lodash-es';
import {
  buildEvTestedDataPath,
  buildFuelCostsPath,
  FeatureSpecsEntities,
  FeatureSpecsPaths,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import {
  getCustomMapFeatures,
  getSectionAnchor,
  includeAdditionalFeatures,
  isMatchKeyword,
} from 'site-modules/features-specs/utils/features-specs';
import {
  COLORS_FEATURES,
  CUSTOM_MAPPING,
  CUSTOM_SECTION_ANCHOR,
} from 'site-modules/features-specs/constants/features-specs';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { LoadingDots } from 'site-modules/shared/components/loading-dots/loading-dots';
import { AnchorNavContent } from 'site-modules/shared/components/anchor-nav-sticky/anchor-nav-content';
import { KeywordInput } from 'site-modules/features-specs/components/keyword-input/keyword-input';
import { InsightAd } from 'site-modules/shared/components/native-ad/insight-ad/insight-ad';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { FeaturesSection } from 'site-modules/features-specs/components/features-section/features-section';
import { MiniInventoryModule } from 'site-modules/shared/components/mini-inventory-module/mini-inventory-module';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { InventorySection } from 'site-modules/features-specs/components/inventory-section/inventory-section';
import { NewPricing } from 'site-modules/features-specs/components/new-pricing/new-pricing';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { VisitorEntities } from 'client/data/models/visitor';
import { getInitialSearchFilter } from 'site-modules/shared/utils/mini-inventory/mini-inventory';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

const KEYWORD_CREATIVE_ID = 'feature_search';
const LOADING_SECTIONS_NUMBER = 10;
const PRICING_SECTION_POSITION = 'Comfort & Convenience';

export function FeaturesSpecsDetailsUI({
  vehicle,
  params,
  style = null,
  trimName = null,
  fuelCostsData = null,
  evTestedData = null,
  visitorLocation = null,
  pageName = null,
  isMobile = false,
}) {
  const [keyword, setKeyword] = useState('');
  const {
    year,
    make: { name: makeName, slug: makeSlug },
    model: { name: modelName, slug: modelSlug },
  } = vehicle;
  const isUsed = params.isUsed;

  function clearKeyword() {
    setKeyword('');
  }

  const styleId = style?.id;
  const isElectric = style?.styleAttributes?.electric;

  const { filteredOrderedFeatures, filteredOrderedFeaturesNames, navList } = useMemo(
    () => {
      if (!styleId) {
        return {};
      }
      const additionalCategoryMap = {
        Engine: {
          category: 'Towing & Hauling',
          features: getCustomMapFeatures(style, CUSTOM_MAPPING['Towing & Hauling']),
        },
        Measurements: {
          category: 'Colors',
          features: COLORS_FEATURES,
        },
      };

      const orderedFeatures = get(style, 'orderedFeatures', []).map(section => ({
        ...section,
        features: section.features.filter(({ name }) => isMatchKeyword(name, keyword)),
        additionalCategory: {
          ...additionalCategoryMap[section.category],
          features: additionalCategoryMap[section.category]?.features?.filter(({ name }) =>
            isMatchKeyword(name, keyword)
          ),
          hasCategory: !!additionalCategoryMap[section.category]?.features?.length,
        },
      }));

      const filteredFeatures = orderedFeatures.filter(
        ({ features, additionalCategory }) => !!features.length || !!additionalCategory?.features?.length
      );

      const anchorNavList = flatten(
        orderedFeatures.map(({ category, additionalCategory }) =>
          compact([
            getSectionAnchor(category, isElectric),
            additionalCategory?.hasCategory && getSectionAnchor(additionalCategory.category, isElectric),
          ])
        )
      );

      if (!isUsed && isMobile) {
        const comfortPosition = anchorNavList.findIndex(({ name }) => name === PRICING_SECTION_POSITION);
        anchorNavList.splice(comfortPosition + 1, 0, CUSTOM_SECTION_ANCHOR.PRICING);
      }

      return {
        navList: anchorNavList,
        filteredOrderedFeatures: filteredFeatures,
        filteredOrderedFeaturesNames: flatten(filteredFeatures.map(({ features }) => features))
          .map(({ name }) => name)
          .join(),
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [styleId, isElectric, keyword, isUsed, isMobile]
  );

  useEffect(() => {
    if (!keyword) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.VIEW_SEARCH_RESULT,
        subaction_name: filteredOrderedFeaturesNames
          ? TrackingConstant.VIEW_AUTO_COMPLETE_RESULT
          : TrackingConstant.EMPTY_AUTO_COMPLETE_RESULT,
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        creative_id: KEYWORD_CREATIVE_ID,
        input: keyword,
        autocomplete_results: filteredOrderedFeaturesNames || undefined,
      },
    });
  }, [keyword, filteredOrderedFeaturesNames]);

  if (!style) {
    /**
     * Add a placeholder for below fold
     * 1) to avoid a user on slow connection from bouncing by telling them content is coming,
     * 2) to avoid CLS
     *    Height of the placeholder is set to be around 5k px. Reasoning for this is the following
     *    The height of this page content after data loads is over 7k. Only the first 900px is preloaded. Without a
     *    below-fold placeholder of 1-2k in height a user on large monitor would see the footer above the fold later
     *    pushed down by loaded content causing CLS. SideNote, deferring the footer to CSR would have been an alternative
     *    way to avoid this CLS.
     *    As to the reason for why 5k instead of 1-2k, this was to reduce negative CLS impact on back button browsing.
     *    The closer to actual height the more CLS will be avoided on back button browsing. At the same time, without ads
     *    and inventory, in case not available in some regions, the height is 6k. 5k for placeholder was chosen to achieve
     *    a balance between
     *    1) leaving a 2k buffer to avoid a placeholder bigger than actual content for above scenario + future reductions of content
     *    2) at the same time reducing negative CLS impact for back button browsing to a section between 0 and 5k.
     */
    return (
      <>
        {/* inline style to avoid having to set criticalCssHeight to 2000px which is easy to miss */}
        <style>
          {`
            .below-fold-placeholder .loading-dots-container { text-align: center; }
            .below-fold-placeholder .loading-dots-container + .loading-dots-container { margin-top: 500px; }
         `}
        </style>
        <div className="below-fold-placeholder">
          {new Array(LOADING_SECTIONS_NUMBER).fill().map((_, index) => {
            const key = `loading-${index}`;
            return (
              <div key={key} className="loading-dots-container">
                <LoadingDots />
              </div>
            );
          })}
        </div>
      </>
    );
  }

  const featuresSectionProps = { params, isMobile, style };

  const clearFilterButton = (
    <Button
      size="lg"
      color="link"
      className="clear-btn text-underline mb-2 text-transform-none p-0 text-blue-50 fw-normal"
      onClick={clearKeyword}
    >
      Clear filter
    </Button>
  );

  return (
    <Fragment>
      {navList?.length > 1 && (
        <AnchorNavContent
          navList={navList}
          isMobile={isMobile}
          className="w-100 mb-1_5"
          listClassName="d-flex align-items-end"
          activeClass="link-active text-blue-40 fw-medium"
          linkTrackingId="view_specs_non_docked"
          creativeId="features_specs_sub-nav"
        />
      )}
      <Row className="mt-1_5">
        <Col xs={12} lg={8} xl={7}>
          <KeywordInput
            className="mb-2"
            labelClassName="heading-5 mb-0_5"
            creativeId={KEYWORD_CREATIVE_ID}
            keyword={keyword}
            hasResults={!!filteredOrderedFeatures.length}
            setKeyword={setKeyword}
            clearKeyword={clearKeyword}
            isMobile={isMobile}
          />
          {!!keyword && (
            <Fragment>
              <div className="keyword-result fw-medium mb-0_25">
                {get(filteredOrderedFeatures, 'length') ? (
                  <Fragment>Showing results for &quot;{keyword}&quot;</Fragment>
                ) : (
                  <Fragment>No results for &quot;{keyword}&quot;</Fragment>
                )}
              </div>
              {clearFilterButton}
            </Fragment>
          )}
        </Col>
      </Row>

      {filteredOrderedFeatures.map(({ category, features, additionalCategory }, index) => {
        const key = `${category}-${index}`;

        const currentFeatures = includeAdditionalFeatures({
          features,
          category,
          fuelCostsData,
          evTestedData,
          isPluginElectric: get(style, 'styleAttributes.pluginElectric'),
        });

        return (
          <Fragment key={key}>
            {index === 1 && (
              <InsightAd
                mobile={isMobile}
                horizontalLayout
                wrapperClass="has-box-shadow mx-auto my-2"
                style={{ maxWidth: '752px' }}
              />
            )}

            <FeaturesSection
              {...featuresSectionProps}
              category={category}
              features={currentFeatures}
              keyword={keyword}
            />

            {category === PRICING_SECTION_POSITION && !isUsed && isMobile && (
              <ScrollElement id={CUSTOM_SECTION_ANCHOR.PRICING.hash}>
                <NewPricing className="mb-1 mb-md-1_5" vehicle={vehicle} styleId={styleId} trimName={trimName} />
              </ScrollElement>
            )}
            {category === 'Fuel' && (
              <MiniInventoryModule
                className="mb-3"
                vehicleParams={{ makeName, modelName, makeSlug, modelSlug, year }}
                initialFilter={getInitialSearchFilter({ makeSlug, modelSlug, year })}
                isMobile={isMobile}
              />
            )}
            {category === 'Drive Train' && (
              <Container key="incentives-site-served-ad">
                <Row>
                  <Col
                    xs={12}
                    className="features-section bg-cool-gray-90 rounded-12 px-1 px-md-1_5 py-1_5 mb-1 mb-md-1_5"
                  >
                    <IncentivesAd mobile={isMobile} includeMrectAdCall wrapperClass="w-100" isFeaturesSpecsLayout />
                  </Col>
                </Row>
              </Container>
            )}
            {category === 'Safety' && (
              <BuildPriceAd
                mobile={isMobile}
                key="build-price-site-served-ad"
                wrapperClass="mb-3 d-flex justify-content-center"
                includeMrectAdCall={!isUsed}
              />
            )}
            {category === 'Interior Options' && (
              <div data-test="sponsored-cars-container" key="sponsored-cars-container">
                <Experiment name="ads-12460_dfp" showDefault>
                  <Recipe name="ctrl" isDefault>
                    <SponsoredCars wrapperClassName="mt-2 mt-md-0 mb-2" showBottomDividingLine={false} />
                  </Recipe>
                  <Recipe name="chal">
                    <SponsoredCars wrapperClassName="mt-2 mt-md-0 mb-2" showBottomDividingLine={false} showStackedAds />
                  </Recipe>
                </Experiment>
              </div>
            )}
            {category === 'Frontseats' && (
              <div data-test="native-mrect1" key="native-mrect1">
                <NativeAd
                  wrapperClass="mrect mt-2 mt-md-0 mb-3 mx-auto"
                  adNameWired="mrect"
                  adNameMobile="mmrect"
                  nativeStyle="mediumrectangle"
                  showLabel
                  useMinHeight
                />
              </div>
            )}
            {category === 'Tires and Wheels' && (
              <div data-test="native-mrect2" key="native-mrect2">
                <MrectNative position={'2'} wiredOnly wrapperClass="mrect2 mb-3 hidden-sm-down" useMinHeight />
              </div>
            )}
            {!!additionalCategory?.features?.length && (
              <FeaturesSection
                {...featuresSectionProps}
                category={additionalCategory.category}
                features={additionalCategory.features}
                keyword={keyword}
              />
            )}
          </Fragment>
        );
      })}
      {!!keyword && !!get(filteredOrderedFeatures, 'length') && clearFilterButton}
      <div className="text-center mb-3_5">
        <InventorySection
          visitorLocation={visitorLocation}
          vehicle={vehicle}
          styleId={styleId}
          trimName={trimName}
          params={params}
          pageName={pageName}
        />
      </div>
    </Fragment>
  );
}

FeaturesSpecsDetailsUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  params: FeaturesSpecsParams.isRequired,
  style: FeatureSpecsEntities.FeatureStyleEntity,
  trimName: PropTypes.string,
  evTestedData: VehicleEntities.StyleEvTestedData,
  fuelCostsData: VehicleEntities.StyleFuelCostData,
  visitorLocation: VisitorEntities.Location,
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
};

export const FeaturesSpecsDetails = flow([
  component =>
    connectToModel(component, {
      evTestedData: bindToPath(
        ({ style, params }) =>
          params.year >= EV_TESTED_DATA_START_DATE &&
          !!get(style, 'styleAttributes.electric') &&
          buildEvTestedDataPath([style.id]),
        VehicleModel,
        stylesData => get(stylesData, '[0]')
      ),
    }),
  component =>
    connectToModel(component, {
      style: bindToPath(({ styleId }) => styleId && FeatureSpecsPaths.buildStyleFeaturesPath(styleId), VehicleModel),
      fuelCostsData: bindToPath(({ styleId }) => buildFuelCostsPath(styleId), VehicleModel),
    }),
])(FeaturesSpecsDetailsUI);
